import { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InsightsIcon from '@mui/icons-material/Insights';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';

import { api } from '../api/client';
import type { City, Covering } from '../api/types';
import { PlaceCoverageWindow } from './PlaceCoverageWindow';
import { PlaceDetailsBody, usePlaceDetails } from './PlaceDetailsBody';

/**
 * What is known about one place, opened from a stop in the itinerary.
 *
 * The frame, the coverage window behind it and what the place can be made
 * into. The record itself is PlaceDetailsBody, which the place browser
 * shows in a tab of its own — so a stop and a candidate read the same.
 */

export function PlaceDetailsDialog({
  poiId,
  onClose,
  city,
  basemapUrl,
  onAddToRound,
  onRemoveFromRound,
}: {
  poiId: string | null;
  onClose: () => void;
  /**
   * The city this place is in, and the tiles to draw it on.
   *
   * Only the coverage window uses them, and only for its map; without them
   * it shows the record alone, which is still the whole answer.
   */
  city?: City | null;
  basemapUrl?: string | null;
  /**
   * Takes this place into the round being planned.
   *
   * Here because this is where the deciding happens: somebody browsing the
   * map opens a place, reads who has planned it and how often — the
   * Coverage tab beside these details — and the answer to "is this one
   * worth taking?" is wanted in the same breath. Sending them back to the
   * form to type the name they are already looking at is the long way round.
   *
   * Absent where there is nothing to add it to: a saved round being read
   * rather than planned, or a place already in this one, which offers
   * removal instead.
   */
  onAddToRound?: (poiId: string) => void;
  /**
   * Drops this place from the round being planned.
   *
   * The one action the panel keeps: adding a place is a decision about what
   * the round should include, which belongs in the form beside everything
   * else it is asked to include — but dropping one is a decision about the
   * place in front of you, made while reading about it. Absent means there
   * is nothing to drop it from: a saved round, or a place not in this one.
   */
  onRemoveFromRound?: (poiId: string) => void;
}) {
  const { details, error } = usePlaceDetails(poiId);
  /*
    The coverage window, over this one.

    It used to be a tab here, which made a wide report read in a narrow
    column and left the details underneath it unreachable without switching
    back. It is the same window the round's Coverage tab opens instead, and
    it carries a way back to these details rather than closing outright.
  */
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverings, setCoverings] = useState<Covering[] | null>(null);
  const [coverageFailed, setCoverageFailed] = useState(false);

  useEffect(() => {
    setCoverageOpen(false);
    setCoverings(null);
    setCoverageFailed(false);
  }, [poiId]);

  /*
    Asked for when the window is opened, not on every place somebody looks
    at: most of them are read and closed, and the panel should not spend a
    query on a question nobody asked.
  */
  useEffect(() => {
    if (!coverageOpen || !details || coverings !== null) return;

    let current = true;
    void api.poiCoverage(details.poi.cityId, [details.poi.id])
      .then((entries) => {
        // A place with no history is left out of the reply, not empty in it.
        if (current) setCoverings(entries[0]?.coverings ?? []);
      })
      .catch(() => { if (current) setCoverageFailed(true); });

    return () => { current = false; };
  }, [coverageOpen, details, coverings]);

  return (
    <Dialog open={Boolean(poiId)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        {details?.poi.name ?? 'Place details'}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <PlaceDetailsBody details={details} error={error} />
      </DialogContent>

      {details && (
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          {/*
            What the record says about this place, in the window that is
            wide enough for it. A button rather than a tab: the report is a
            map, a strip of figures, a list of people and a list of plans,
            and a narrow column beside the photographs did none of it
            justice.
          */}
          <Button
            size="small"
            startIcon={<InsightsIcon />}
            onClick={() => setCoverageOpen(true)}
            sx={{ mr: 'auto' }}
          >
            Coverage
          </Button>
          {/*
            One or the other, never both: a place is either in the round or
            it is not, and the panel says which by what it offers to do
            about it.
          */}
          {onRemoveFromRound ? (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => { onRemoveFromRound(details.poi.id); onClose(); }}
            >
              Remove from this round
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { onAddToRound?.(details.poi.id); onClose(); }}
            >
              Add to this round
            </Button>
          )}
        </DialogActions>
      )}

      {/*
        Over the panel, with the way back to it.

        Mounted inside this dialog so closing the place closes the report
        with it; `onBack` returns to the details rather than dismissing
        both, which is what somebody comparing a place against its record
        wants to do several times before deciding.
      */}
      {coverageOpen && details && (
        <PlaceCoverageWindow
          place={{
            key: details.poi.id,
            name: details.poi.name,
            lat: details.poi.lat,
            lng: details.poi.lng,
            poiId: details.poi.id,
          }}
          coverings={coverings ?? []}
          {...(city ? { city } : {})}
          {...(basemapUrl !== undefined ? { basemapUrl } : {})}
          onBack={() => setCoverageOpen(false)}
          backLabel="Back to the place"
          onClose={() => { setCoverageOpen(false); onClose(); }}
        />
      )}

      {coverageFailed && coverageOpen && (
        <Snackbar open autoHideDuration={6000} onClose={() => setCoverageFailed(false)}>
          <Alert severity="warning" variant="filled">
            The coverage record could not be read.
          </Alert>
        </Snackbar>
      )}
    </Dialog>
  );
}
